import { useParams, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { ArrowLeft, MapPin, Calendar, X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";

// ── Gallery normaliser (array | JSON string | object) ─────────
function normaliseGallery(raw) {
  let arr = raw;
  if (typeof arr === "string") { try { arr = JSON.parse(arr); } catch { return []; } }
  if (arr && !Array.isArray(arr) && typeof arr === "object") arr = Object.values(arr);
  if (!Array.isArray(arr)) return [];
  return arr
    .map(item => typeof item === "string" ? item.trim()
      : (item && typeof item === "object" ? (item.url || item.src || "") : ""))
    .filter(u => u && (u.startsWith("http") || u.startsWith("/")));
}

// ── Masonry column splitter ───────────────────────────────────
function splitIntoColumns(items, cols) {
  const columns = Array.from({ length: cols }, () => []);
  items.forEach((item, i) => columns[i % cols].push({ item, index: i }));
  return columns;
}

function GalleryImage({ src, alt, index, onClick, priority }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  if (error) return null;
  return (
    <button
      type="button"
      onClick={() => onClick(index)}
      className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8914A] group gsap-image-hover"
      data-gsap="image"
      data-testid={`pw-gallery-img-${index}`}
      aria-label={`View ${alt} photo ${index + 1}`}
    >
      {!loaded && (
        <div className="w-full bg-[#E4DCCD] animate-pulse" style={{ minHeight: "200px", aspectRatio: "4/5" }} />
      )}
      <img
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={["w-full h-auto block transition-opacity duration-500 group-hover:opacity-90",
          loaded ? "opacity-100" : "opacity-0 absolute top-0 left-0"].join(" ")}
        style={{ display: "block" }}
      />
    </button>
  );
}

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);
  if (index === null || !images[index]) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95" onClick={onClose} data-testid="pw-lightbox">
      <img src={images[index]} alt={`Photo ${index + 1}`} className="max-w-[92vw] max-h-[90vh] object-contain" onClick={e => e.stopPropagation()} />
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white p-2" aria-label="Close"><X size={24} /></button>
      {index > 0 && (
        <button onClick={e => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" aria-label="Previous"><ChevronLeft size={36} /></button>
      )}
      {index < images.length - 1 && (
        <button onClick={e => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2" aria-label="Next"><ChevronRight size={36} /></button>
      )}
    </div>
  );
}

export default function PreWeddingDetail() {
  const { slug } = useParams();
  const { preweddingSessions } = useSite() || {};
  const session = (preweddingSessions || []).find(s => s.slug === slug);
  const [lightbox, setLightbox] = useState(null);

  const gallery = normaliseGallery(session?.gallery).map(fileUrl);
  const openLightbox = useCallback((i) => setLightbox(i), []);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox(i => (i > 0 ? i - 1 : i)), []);
  const next = useCallback(() => setLightbox(i => (i < gallery.length - 1 ? i + 1 : i)), [gallery.length]);

  // Branded empty state
  if (!session) {
    return (
      <div className="bg-[#F5EFE4] text-[#111111] min-h-screen flex items-center justify-center px-6" data-testid="pw-not-found">
        <div className="text-center">
          <div className="eyebrow text-[#B8914A] text-[10px]">Pre-Wedding</div>
          <h1 className="font-display-italic text-5xl md:text-6xl mt-4">Session not found</h1>
          <p className="mt-4 text-[rgba(17,17,17,0.6)] text-sm">This pre-wedding album may have moved or is being curated.</p>
          <Link to="/pre-wedding" className="btn-outline-dark mt-10 inline-flex" data-testid="pw-back-empty">
            <ArrowLeft size={12} /> Back to Pre-Wedding
          </Link>
        </div>
      </div>
    );
  }

  const cover = session.cover || gallery[0] || "";
  const coverPos = session.coverPositionDesktop && session.coverPositionDesktop !== "center center"
    ? session.coverPositionDesktop : "center 32%";

  return (
    <div className="bg-[#F5EFE4] text-[#111111] min-h-screen" data-testid="prewedding-detail">

      {/* Hero cover */}
      <section className="cinematic-hero relative h-[78vh] overflow-hidden" data-gsap="hero">
        {cover ? (
          <img src={fileUrl(cover)} alt={session.couple} data-gsap="hero-media"
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: session.coverFit || "cover", objectPosition: coverPos }} />
        ) : (
          <div className="absolute inset-0 bg-[#DDD5C8]" />
        )}
        <div className="hero-grain cinematic-grain" aria-hidden="true" />
        <div className="hero-overlay cinematic-overlay absolute inset-0 bg-gradient-to-b from-black/25 via-black/20 to-black/72" />
        <div className="absolute inset-0 flex items-end z-10">
          <div className="max-w-[1380px] mx-auto w-full px-6 md:px-12 pb-14 md:pb-22 text-[#F5EFE4]">
            <Link to="/pre-wedding" className="eyebrow text-[10px] inline-flex items-center gap-2 text-[#F5EFE4]/70 hover:text-[#E6C988] transition" data-testid="pw-back" data-gsap="hero-kicker">
              <ArrowLeft size={11} /> Back to Pre-Wedding
            </Link>
            <div className="eyebrow text-[#E6C988] mt-6 text-[10px]" data-gsap="hero-kicker">{session.type}</div>
            <h1 className="font-display-italic text-5xl md:text-7xl lg:text-8xl mt-2 leading-[0.92]" data-gsap="hero-title">{session.couple}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 eyebrow text-[10px] text-[#F5EFE4]/75" data-gsap="hero-subtitle">
              {session.location && <span className="flex items-center gap-2"><MapPin size={11} /> {session.location}</span>}
              {session.date && <span className="flex items-center gap-2"><Calendar size={11} /> {session.date}</span>}
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      {(session.fullStory || session.story) && (
        <section className="py-16 md:py-28 px-6 md:px-10" data-gsap="reveal">
          <div className="max-w-3xl mx-auto text-center">
            <div className="eyebrow text-[#B8914A] text-[10px]">The Story</div>
            <span className="gold-line gold-line-center block my-6" />
            <p className="font-display-italic text-xl md:text-2xl lg:text-3xl leading-[1.5] text-[rgba(17,17,17,0.85)]">
              {session.fullStory || session.story}
            </p>
          </div>
        </section>
      )}

      {/* Gallery — natural ratio masonry, no face crop */}
      <section className="pb-16 md:pb-24 px-4 md:px-6" data-testid="pw-detail-gallery" data-gsap="reveal">
        {gallery.length === 0 ? (
          <div className="max-w-3xl mx-auto text-center py-10">
            <p className="eyebrow text-[10px] text-[rgba(17,17,17,0.4)]">No gallery images uploaded yet.</p>
          </div>
        ) : (
          <>
            <div className="hidden lg:flex gap-3" aria-label="Pre-wedding gallery" data-gsap="stagger">
              {splitIntoColumns(gallery, 3).map((col, ci) => (
                <div key={ci} className="flex-1 flex flex-col gap-3">
                  {col.map(({ item, index }) => (
                    <GalleryImage key={index} src={item} alt={session.couple} index={index} onClick={openLightbox} priority={index < 3} />
                  ))}
                </div>
              ))}
            </div>
            <div className="hidden md:flex lg:hidden gap-3" aria-label="Pre-wedding gallery" data-gsap="stagger">
              {splitIntoColumns(gallery, 2).map((col, ci) => (
                <div key={ci} className="flex-1 flex flex-col gap-3">
                  {col.map(({ item, index }) => (
                    <GalleryImage key={index} src={item} alt={session.couple} index={index} onClick={openLightbox} priority={index < 2} />
                  ))}
                </div>
              ))}
            </div>
            <div className="flex md:hidden flex-col gap-3" aria-label="Pre-wedding gallery" data-gsap="stagger">
              {gallery.map((item, index) => (
                <GalleryImage key={index} src={item} alt={session.couple} index={index} onClick={openLightbox} priority={index === 0} />
              ))}
            </div>
            <p className="mt-6 text-center eyebrow text-[10px] text-[rgba(17,17,17,0.35)]">
              {gallery.length} photograph{gallery.length !== 1 ? "s" : ""}
            </p>
          </>
        )}
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 text-center bg-[#111111] text-[#F5EFE4]" data-gsap="reveal">
        <h2 className="font-display-italic text-4xl md:text-6xl">Loved this session?</h2>
        <p className="mt-4 text-[rgba(245,239,228,0.6)] text-sm">Let's plan yours, beautifully.</p>
        <Link to="/contact" className="btn-gold-fill mt-10 inline-flex" data-testid="pw-detail-cta">
          Enquire Now <ChevronRight size={13} />
        </Link>
      </section>

      <Lightbox images={gallery} index={lightbox} onClose={closeLightbox} onPrev={prev} onNext={next} />
    </div>
  );
}
